import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    href: String,
    turboFrame: { type: String, default: "remote_modal" },
    candidateJobId: Number
  }

  connect() {
    this.handleCandidateViewed = this.handleCandidateViewed.bind(this)
    window.addEventListener('candidate:viewed', this.handleCandidateViewed)

    const currentId = Number(this.candidateJobIdValue || this.element.dataset.candidateJobId)
    if (currentId && localStorage.getItem(`viewed_cj_${currentId}`)) {
      this.markAsViewed(false)
    }
  }

  disconnect() {
    window.removeEventListener('candidate:viewed', this.handleCandidateViewed)
  }

  handleCandidateViewed(event) {
    const viewedId = Number(event.detail?.candidateJobId)
    const currentId = Number(this.candidateJobIdValue || this.element.dataset.candidateJobId)
    if (viewedId && currentId && viewedId === currentId) {
      this.markAsViewed()
    }
  }

  markAsViewed(persist = true) {
    const currentId = Number(this.candidateJobIdValue || this.element.dataset.candidateJobId)
    if (persist && currentId) {
      try {
        localStorage.setItem(`viewed_cj_${currentId}`, 'true')
      } catch (e) {}
    }

    this.element.classList.remove('candidate-card-unviewed', 'matchmaking-card-unread')
    this.element.classList.add('candidate-card-viewed')
    const nameEl = this.element.querySelector('.candidate-card-name')
    if (nameEl) {
      nameEl.classList.remove('fw-bold')
    }
  }

  click(event) {
    if (event.target.closest('.dropdown') || event.target.closest('.dropdown-menu')) {
      return
    }

    if (event.target.closest('.link-icons')) {
      return
    }

    if (event.target.closest('[onclick*="stopPropagation"]')) {
      return
    }

    if (!this.hasHrefValue) {
      return
    }

    if (this.element.classList.contains("candidate-card-loading")) {
      event.preventDefault()
      event.stopPropagation()
      return
    }

    this.markAsViewed()
    event.preventDefault()
    event.stopPropagation()

    document.querySelectorAll('.candidate-card-loading').forEach((card) => {
      card.classList.remove('candidate-card-loading')
    })

    let frame = document.getElementById(this.turboFrameValue)
    if (!frame) {
      frame = document.createElement("turbo-frame")
      frame.id = this.turboFrameValue
      document.body.appendChild(frame)
    }

    this.element.classList.add("candidate-card-loading")

    let timeoutId = null
    const cleanupLoading = () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
        timeoutId = null
      }
      this.element.classList.remove("candidate-card-loading")
      document.removeEventListener("turbo:frame-load", onFrameSuccess)
      document.removeEventListener("turbo:frame-render", onFrameSuccess)
      document.removeEventListener("turbo:frame-missing", onFrameMissing)
      document.removeEventListener("turbo:fetch-request-error", onFetchError)
    }

    const onFrameSuccess = (e) => {
      if (e.target && e.target.id === this.turboFrameValue) {
        cleanupLoading()
      }
    }

    const onFrameMissing = (e) => {
      if (e.target && e.target.id === this.turboFrameValue) {
        e.preventDefault()
        cleanupLoading()
      }
    }

    const onFetchError = () => {
      cleanupLoading()
    }

    document.addEventListener("turbo:frame-load", onFrameSuccess)
    document.addEventListener("turbo:frame-render", onFrameSuccess)
    document.addEventListener("turbo:frame-missing", onFrameMissing)
    document.addEventListener("turbo:fetch-request-error", onFetchError)
    timeoutId = setTimeout(cleanupLoading, 4000)

    const targetUrl = new URL(this.hrefValue, window.location.origin).href
    const rawSrc = frame.getAttribute("src")
    const currentFrameSrc = rawSrc ? new URL(rawSrc, window.location.origin).href : ""

    if (currentFrameSrc === targetUrl) {
      if (typeof frame.reload === "function") {
        frame.reload()
      } else {
        frame.src = ""
        setTimeout(() => { frame.src = this.hrefValue }, 10)
      }
      return
    }

    frame.src = this.hrefValue
  }

  retryMatchmaking(event) {
    event.preventDefault()
    event.stopPropagation()

    const url = event.params.url
    if (!url) return

    const button = event.currentTarget
    const originalHtml = button.innerHTML
    const feedback = this.ensureRetryFeedbackElement(button)
    const startedAt = Date.now()
    const minSpinnerTimeMs = 3500

    button.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i>'
    button.classList.add('disabled')
    button.setAttribute('aria-busy', 'true')

    feedback.textContent = 'Reenviando... pode demorar.'
    feedback.className = 'retry-matchmaking-feedback text-muted small'

    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content

    fetch(url, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json'
      },
      credentials: 'same-origin'
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        button.innerHTML = '<i class="fa-solid fa-check"></i>'
        button.classList.remove('text-warning')
        button.classList.add('text-success')

        feedback.textContent = 'Reenvio solicitado.'
        feedback.className = 'retry-matchmaking-feedback text-success small'
      })
      .catch(() => {
        button.innerHTML = '<i class="fa-solid fa-triangle-exclamation"></i>'
        button.classList.remove('text-warning')
        button.classList.add('text-danger')

        feedback.textContent = 'Falha no reenvio. Tente novamente.'
        feedback.className = 'retry-matchmaking-feedback text-danger small'
      })
      .finally(() => {
        const elapsed = Date.now() - startedAt
        const remaining = Math.max(minSpinnerTimeMs - elapsed, 0)

        setTimeout(() => {
          button.innerHTML = originalHtml
          button.classList.remove('disabled', 'text-success', 'text-danger')
          button.classList.add('text-warning')
          button.removeAttribute('aria-busy')
        }, remaining)

        setTimeout(() => {
          feedback.textContent = ''
        }, 6500)
      })
  }

  ensureRetryFeedbackElement(button) {
    const badgesContainer = button.closest('#candidate_job-card-badges')
    const feedbackContainer = badgesContainer?.parentElement || button.parentElement
    let feedback = feedbackContainer?.querySelector('.retry-matchmaking-feedback')

    if (!feedback && feedbackContainer) {
      feedback = document.createElement('div')
      feedback.className = 'retry-matchmaking-feedback small'
      feedback.style.flexBasis = '100%'
      feedback.style.lineHeight = '1.2'
      feedback.style.marginTop = '4px'
      feedbackContainer.appendChild(feedback)
    }

    return feedback || document.createElement('span')
  }
}
